import { reactive } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'

interface SwipeActionOptions<TItem> {
  threshold?: number
  maxMultiplier?: number
  getId: (item: TItem) => string
  isDisabled?: () => boolean
  onCancelCompetingGesture?: () => void
  onTrigger: (item: TItem) => void | Promise<void>
}

const DEFAULT_THRESHOLD = 110
const DEFAULT_MAX_MULTIPLIER = 1.25

const triggerHapticFeedback = () => {
  if (!('vibrate' in navigator)) return

  try {
    navigator.vibrate(30)
  } catch (error) {
    logService.debug(LOG_CATEGORIES.UI, 'Swipe haptic feedback unavailable', { error })
  }
}

export function useSwipeAction<TItem>(options: SwipeActionOptions<TItem>) {
  const translateX = reactive<Record<string, number>>({})
  const transitioning = reactive<Record<string, boolean>>({})
  const threshold = options.threshold ?? DEFAULT_THRESHOLD
  const max = threshold * (options.maxMultiplier ?? DEFAULT_MAX_MULTIPLIER)

  let activeId: string | null = null
  let startX = 0
  let startY = 0
  let thresholdHit = false
  let isScrolling = false
  let suppressNextClick = false

  const resetActive = (id: string) => {
    transitioning[id] = true
    translateX[id] = 0
    activeId = null
  }

  const start = (event: TouchEvent, item: TItem) => {
    if (options.isDisabled?.() || event.touches.length !== 1) return

    const id = options.getId(item)
    const touch = event.touches[0]
    activeId = id
    startX = touch.clientX
    startY = touch.clientY
    thresholdHit = false
    isScrolling = false
    suppressNextClick = false
    transitioning[id] = false
  }

  const move = (event: TouchEvent, item: TItem) => {
    const id = options.getId(item)
    if (activeId !== id || event.touches.length !== 1) return

    const touch = event.touches[0]
    const dx = touch.clientX - startX
    const dy = touch.clientY - startY
    const hasHorizontalIntent = Math.abs(dx) > Math.abs(dy) + 5

    if (isScrolling) return
    if (Math.abs(dy) > Math.abs(dx) + 5) {
      isScrolling = true
      options.onCancelCompetingGesture?.()
      translateX[id] = 0
      return
    }

    if (hasHorizontalIntent && event.cancelable) {
      event.preventDefault()
    }

    if (Math.abs(dx) > 6) {
      options.onCancelCompetingGesture?.()
    }

    translateX[id] = Math.max(-max, Math.min(max, dx))
    if (!thresholdHit && Math.abs(dx) >= threshold) {
      thresholdHit = true
      triggerHapticFeedback()
    }
  }

  const end = (item: TItem) => {
    const id = options.getId(item)
    if (activeId !== id) return

    if (thresholdHit && !isScrolling) {
      suppressNextClick = true
      void options.onTrigger(item)
    } else if (Math.abs(translateX[id] ?? 0) > 8) {
      suppressNextClick = true
    }

    resetActive(id)
  }

  const cancel = (item: TItem) => {
    const id = options.getId(item)
    if (activeId !== id) return
    resetActive(id)
  }

  const consumeSuppressedClick = () => {
    if (!suppressNextClick) return false
    suppressNextClick = false
    return true
  }

  const clearTransition = (item: TItem) => {
    transitioning[options.getId(item)] = false
  }

  return {
    translateX,
    transitioning,
    start,
    move,
    end,
    cancel,
    consumeSuppressedClick,
    clearTransition,
  }
}
